import { Component, OnInit, OnDestroy, ViewChild, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { AuthService, User } from './services/auth.service';
import { MatDividerModule } from '@angular/material/divider';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { GuardadosService } from './services/guardados.service';
import { Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { ProductoCSV } from './models/producto-csv.model';
import { TitleService } from './services/title.service';
import { ThemeService } from './core/services/theme.service';
import { ToastContainerComponent } from './shared/components/toast-container/toast-container.component';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatBadgeModule,
    RouterModule,
    MatDividerModule,
    MatSidenavModule,
    MatListModule,
    ClipboardModule,
    ToastContainerComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  currentYear = new Date().getFullYear();
  isAuthenticated = false;
  currentUser: User | null = null;
  
  isHandset$: Observable<boolean>;
  @ViewChild('drawer') drawer!: MatSidenav;
  private cdr = inject(ChangeDetectorRef);
  private guardadosService = inject(GuardadosService);
  private themeService = inject(ThemeService);
  productosGuardados$: Observable<string[]>;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private titleService: TitleService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private breakpointObserver: BreakpointObserver,
    private iconRegistry: MatIconRegistry,
  ) {
    // Registrar Material Symbols como font set adicional
    this.iconRegistry.registerFontClassAlias('material-symbols', 'material-symbols-outlined');
    this.iconRegistry.setDefaultFontSetClass('material-symbols-outlined');

    this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset)
      .pipe(
        map(result => result.matches),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    this.productosGuardados$ = this.guardadosService.guardados$;
  }

  ngOnInit(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.isAuthenticated = !!user;
        this.currentUser = user;
        this.cdr.markForCheck();
      });

    // Aviso cuando la sesión está por expirar (solo usuarios sin rememberMe)
    this.authService.sesiónPorExpirar$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const snackRef = this.snackBar.open(
          'Tu sesión expira en 5 minutos.',
          'Extender sesión',
          { duration: 4 * 60 * 1000, panelClass: 'session-warning-snack' }
        );
        snackRef.onAction().pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.authService.renovarToken().subscribe();
        });
      });
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        map(route => route.snapshot.data['title'] || 'LocalCenter'),
        takeUntil(this.destroy$)
      )
      .subscribe((title: string) => {
        this.titleService.setTitle(title);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async verDetalles(producto: ProductoCSV): Promise<void> {
    const { ProductoDetalleComponent } = await import('./components/producto-detalle/producto-detalle.component');
    this.dialog.open(ProductoDetalleComponent, {
      width: '600px',
      data: producto
    });
  }


  async openLoginDialog(): Promise<void> {
    const { LoginComponent } = await import('./pages/auth/login/login.component');
    const dialogRef = this.dialog.open(LoginComponent, {
      width: '400px',
      panelClass: 'login-dialog-container',
      disableClose: false
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        if (result.success) {
          this.router.navigate(['/crear']);
        } else if (result.action === 'register') {
        }
      }
    });
  }

  logout(): void {
    this.authService.logout();
  }
  
  closeDrawerIfMobile(): void {
    if (this.drawer && this.isHandset$ && this.breakpointObserver.isMatched(Breakpoints.Handset)) {
      this.drawer.close();
    }
  }

  removerProductoGuardado(codigo: string): void {
    this.guardadosService.removeProducto(codigo);
    // Opcional: Notificar que se removió
    // this.notificationService.showInfo(`Producto ${codigo} quitado de guardados`);
  }

}